import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import type {
  AdminAuction,
  AdminAuctionRegistration,
  PatchAdminV1StoresStoreIdAuctionsIdBody,
  PostAdminV1StoresStoreIdAuctionsBody,
} from '@nucleus-commerce/api-client/admin';

import { auctionsApi } from '../api/auctions-api';

import { auctionKeys } from './auction-keys';

/** The answer is the whole auction: it replaces the detail, and lists and bids refetch. */
async function storeAuction(queryClient: QueryClient, storeId: string, auction: AdminAuction) {
  queryClient.setQueryData(auctionKeys.detail(storeId, auction.id), auction);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: auctionKeys.lists(storeId) }),
    queryClient.invalidateQueries({ queryKey: auctionKeys.allBids(storeId, auction.id) }),
    queryClient.invalidateQueries({ queryKey: auctionKeys.offers(storeId, auction.id) }),
  ]);
}

export function useCreateAuction(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdAuctionsBody) => auctionsApi.create(storeId, body),
    meta: { successMessage: 'auctions:toasts.created', silentError: true },
    onSuccess: (auction) => storeAuction(queryClient, storeId, auction),
  });
}

export function useUpdateAuction(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdAuctionsIdBody }) =>
      auctionsApi.update(storeId, id, body),
    meta: { successMessage: 'auctions:toasts.updated', silentError: true },
    onSuccess: (auction) => storeAuction(queryClient, storeId, auction),
  });
}

export function useCancelAuction(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      auctionsApi.cancel(storeId, id, reason),
    meta: { successMessage: 'auctions:toasts.cancelled', silentError: true },
    onSuccess: (auction) => storeAuction(queryClient, storeId, auction),
  });
}

/**
 * A sale places the winner's order, so the store's orders are stale too. The key is spelled out
 * (orderKeys.all) because importing the orders door from here closes an import cycle through the
 * store catalog, which shows the auction panel.
 */
export function useEndAuction(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => auctionsApi.end(storeId, id),
    meta: { successMessage: 'auctions:toasts.ended', silentError: true },
    onSuccess: async (auction) => {
      await storeAuction(queryClient, storeId, auction);
      if (auction.orderId) {
        await queryClient.invalidateQueries({ queryKey: ['stores', storeId, 'orders'] });
      }
    },
  });
}

/** Cancels the winner's unpaid order too, so the store's orders refetch (key spelled out as above). */
export function useSecondChance(storeId: string, auctionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (expiresInHours: number) =>
      auctionsApi.secondChance(storeId, auctionId, expiresInHours),
    meta: { successMessage: 'auctions:toasts.offerSent', silentError: true },
    onSuccess: async (auction) => {
      await storeAuction(queryClient, storeId, auction);
      await queryClient.invalidateQueries({ queryKey: ['stores', storeId, 'orders'] });
    },
  });
}

export function useWithdrawOffer(storeId: string, auctionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (offerId: string) => auctionsApi.withdrawOffer(storeId, auctionId, offerId),
    meta: { successMessage: 'auctions:toasts.offerWithdrawn', silentError: true },
    onSuccess: (auction) => storeAuction(queryClient, storeId, auction),
  });
}

export function useRetractBid(storeId: string, auctionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bidId, reason }: { bidId: string; reason: string }) =>
      auctionsApi.retractBid(storeId, auctionId, bidId, reason),
    meta: { successMessage: 'auctions:toasts.retracted', silentError: true },
    onSuccess: (auction) => storeAuction(queryClient, storeId, auction),
  });
}

function useRegistrationChange(
  storeId: string,
  successMessage: string,
  run: (vars: { id: string; reason: string }) => Promise<AdminAuctionRegistration>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    meta: { successMessage, silentError: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: auctionKeys.registrations(storeId) }),
  });
}

export function useBlockBidder(storeId: string) {
  return useRegistrationChange(storeId, 'auctions:toasts.blocked', ({ id, reason }) =>
    auctionsApi.block(storeId, id, reason),
  );
}

export function useUnblockBidder(storeId: string) {
  return useRegistrationChange(storeId, 'auctions:toasts.unblocked', ({ id }) =>
    auctionsApi.unblock(storeId, id),
  );
}
